import { Alert, AlertColor, AlertTitle, Snackbar } from '@mui/material';
import { createContext, ReactNode, useContext, useState } from 'react';

export interface ShowToastProps {
  severity: AlertColor;
  header?: string;
  body: string;
}

export interface ToastContext {
  showToast: (showToastProps: ShowToastProps) => void;
}

export interface ToastContextProviderProps {
  children: ReactNode;
}

export const ToastContext = createContext<ToastContext>({
  showToast: (_showToastProps: ShowToastProps) => {},
});

export const ToastProvider = ({ children }: ToastContextProviderProps) => {
  const [toast, setToast] = useState<ShowToastProps>({
    severity: 'info',
    header: '',
    body: '',
  });
  const [open, setOpen] = useState<boolean>(false);

  const showToast = (showToastProps: ShowToastProps) => {
    setOpen(true);
    setToast(showToastProps);
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <Snackbar
        onClose={(_, reason) => {
          if (reason === 'timeout') {
            setOpen(false);
          }
        }}
        open={open}
        autoHideDuration={6000}
        disableWindowBlurListener
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        sx={{
          mt: 'var(--safe-area-inset-top)',
        }}
      >
        <Alert severity={toast.severity} onClose={() => setOpen(false)}>
          {toast.header && <AlertTitle>{toast.header}</AlertTitle>}
          {toast.body}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
